import React, {useEffect, useRef, useState} from 'react';
import {
  DiscountNotificationType,
  useDiscountNotification,
} from '../hooks/useDiscountNotification';
import {useDiscounts} from '../hooks/useDiscounts';
import {
  GetDiscountsResponse,
  GetDiscountsResponse_Discount,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {DiscountType} from '../enums/DiscountType';
import {useGetActiveDiscountCallback} from '../hooks/useGetActiveDiscount';
import {useIsConsentBannerAccepted} from '@aboutyou/storage';
import {CampaignRefreshPopup} from './CampaignRefreshPopup';
import {usePrevious} from '@aboutyou/ui-components';

const MAX_TIMEOUT_WAIT_IN_MS = 2147483647;

const isPriceReductionCampaign = (discount?: GetDiscountsResponse_Discount) => {
  return discount?.discount?.$case === DiscountType.priceReductionCampaign;
};

export const DiscountsRefresh = () => {
  const {refetch, data, error} = useDiscounts();
  const getDiscountsFetchFailed = Boolean(error);
  const {showNotification} = useDiscountNotification();
  const getActiveDiscount = useGetActiveDiscountCallback();
  const nextUpdate = data?.nextUpdate
    ? new Date(data?.nextUpdate).getTime()
    : undefined;
  const timeout = useRef<NodeJS.Timeout>();
  const now = useRef(new Date().getTime());

  const previousNextUpdate = usePrevious(nextUpdate);

  /**
   * when a campaign end abruptly when someone deletes it or reduce the time in backoffice,
   * the nextUpdate field disapears. In this case we force a refresh of the page
   */
  useEffect(() => {
    if (getDiscountsFetchFailed) return; // do not show popup when fetching discounts failed

    if (previousNextUpdate && !nextUpdate) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setShowCampaignRefreshInfoPopup(true);
    }
  }, [previousNextUpdate, nextUpdate, getDiscountsFetchFailed]);

  const [showCampaignRefreshInfoPopup, setShowCampaignRefreshInfoPopup] =
    useState(false);
  const [campaignId, setCampaignId] = useState<string | undefined>();

  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    if (nextUpdate) {
      const diff = nextUpdate - now.current;
      timeout.current = setTimeout(async () => {
        const previousActiveDiscount = await getActiveDiscount(data?.discounts);

        const previousPriceReductionCampaign = isPriceReductionCampaign(
          previousActiveDiscount,
        );

        const {data: refetchData} = await refetch();
        if (refetchData) {
          const activeDiscount = await getActiveDiscount(refetchData.discounts);

          const currentPriceReductionCampaign =
            isPriceReductionCampaign(activeDiscount);

          /**
           * If we go from a price reduction campaign to no active discount situation or vice versa, we need to reload the page
           * This is because the price reduction campaign key is not part of any GRPC requests, a full reload is required in this case
           */
          if (
            (previousPriceReductionCampaign || currentPriceReductionCampaign) &&
            previousActiveDiscount?.id !== activeDiscount?.id
          ) {
            // eslint-disable-next-line local-rules/set-state-in-use-effect
            setShowCampaignRefreshInfoPopup(true);
            setCampaignId(activeDiscount?.id);
            return;
          }

          // ↓ changes to personal discount cases

          // No previous discounts were available, and a new one started
          if (
            !previousActiveDiscount?.id &&
            refetchData.discounts.length &&
            activeDiscount?.id
          ) {
            showNotification({
              type: DiscountNotificationType.DiscountStart,
            });
            return;
          }

          // A previously available discount ended and no new one available
          if (
            previousActiveDiscount?.id &&
            !refetchData.discounts.length &&
            !activeDiscount?.id
          ) {
            showNotification({
              type: DiscountNotificationType.ExpiredWithEmptyDiscounts,
            });
            return;
          }

          // Switched to a another discount from a discount which is expired now
          if (
            previousActiveDiscount?.id &&
            activeDiscount?.id &&
            previousActiveDiscount?.id !== activeDiscount?.id
          ) {
            showNotification({
              type: DiscountNotificationType.SwitchExpired,
            });
          }
        }
        /**
         * This is required because `setTimeout`'s internals only allow up to 2147483647 milliseconds since
         * timeout values too big to fit into a signed 32-bit integer may cause overflow in FF, Safari, and Chrome,
         * resulting in the timeout being scheduled immediately.
         *
         * https://nodejs.org/api/timers.html#settimeoutcallback-delay-args
         */
      }, Math.min(diff, MAX_TIMEOUT_WAIT_IN_MS));
    }

    return () => timeout.current && clearTimeout(timeout.current);
  }, [nextUpdate]);

  useSyncPersonalDiscountValidUntil(data);

  return showCampaignRefreshInfoPopup ? (
    <CampaignRefreshPopup
      onClose={() => setShowCampaignRefreshInfoPopup(false)}
      campaignId={campaignId}
    />
  ) : null;
};

// storage value is used to detect if the space for the discount on SSR should be preserved as there is a high change a personal discount is still present (which we cannot know on the server as personalized discounts are user specific)
export const PERSONALIZED_DISCOUNT_VALID_UNTIL_KEY =
  'tadarida-personalized-discount-valid-until-v1';
const useSyncPersonalDiscountValidUntil = (
  data: GetDiscountsResponse | undefined,
) => {
  const consentAccepted = useIsConsentBannerAccepted();
  const rawNextUpdate = data?.nextUpdate;
  const hasPersonalizedDiscount = data?.discounts.some(discount => {
    return discount.discount?.$case === DiscountType.personalDiscount;
  });

  useEffect(() => {
    try {
      if (hasPersonalizedDiscount && rawNextUpdate && consentAccepted) {
        window.localStorage.setItem(
          PERSONALIZED_DISCOUNT_VALID_UNTIL_KEY,
          rawNextUpdate as unknown as string,
        );
      } else {
        window.localStorage.removeItem(PERSONALIZED_DISCOUNT_VALID_UNTIL_KEY);
      }
    } catch (e) {
      // ignore
    }
  }, [rawNextUpdate, hasPersonalizedDiscount, consentAccepted]);
};
